import type { ScaleBand } from 'd3-scale';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Label, ReferenceLine, XAxis, YAxis } from 'recharts';

import InfoToolTip from 'DesignSystem/features/InfoTooltip';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from 'DesignSystem/ui/chart';
import type { FrequencyOfUseData } from 'pages/preference-cards/types';
import { formatNumber } from 'utils/helpers';

const TOOLTIP_TEXT = [
  'Distribution of the number of units used per case for the selected item over the past 12 months, weighted by recency',
];

const chartConfig = {
  count_cases_last_3_months_percent: {
    label: 'Last 3 mos',
    color: '#172554',
  },
  count_cases_last_3_6_months_percent: {
    label: '3-6 mos ago',
    color: '#1E40AF',
  },
  count_cases_last_6_12_months_percent: {
    label: '6-12 mos ago',
    color: '#3B82F6',
  },
} satisfies ChartConfig;

const chartExtendedLabelMap: Record<string, string> = {
  count_cases_last_3_months_percent: 'Last 3 months',
  count_cases_last_3_6_months_percent: '3-6 months ago',
  count_cases_last_6_12_months_percent: '6-12 months ago',
};

type FrequencyOfUseWeightedChartProps = {
  frequency: FrequencyOfUseData[];
  caseCount: number;
};

export function FrequencyOfUseWeightedChart({ frequency, caseCount }: FrequencyOfUseWeightedChartProps) {
  const data = React.useMemo(() => {
    return frequency.map((f) => {
      return {
        count_items: `${f.count_items ?? 0}`,
        count_cases_last_3_months_value: f.count_cases_0_3m ?? 0,
        count_cases_last_3_6_months_value: f.count_cases_3_6m ?? 0,
        count_cases_last_6_12_months_value: f.count_cases_6_12m ?? 0,

        count_cases_last_3_months_percent: formatYAxisValue(f.count_cases_0_3m ?? 0, caseCount),
        count_cases_last_3_6_months_percent: formatYAxisValue(f.count_cases_3_6m ?? 0, caseCount),
        count_cases_last_6_12_months_percent: formatYAxisValue(f.count_cases_6_12m ?? 0, caseCount),
        count_cases_percent: formatYAxisValue(f.count_cases ?? 0, caseCount),
      };
    });
  }, [frequency, caseCount]);

  const barRadii = React.useMemo(() => {
    const barOrder: (keyof typeof chartConfig)[] = [
      'count_cases_last_3_months_percent',
      'count_cases_last_3_6_months_percent',
      'count_cases_last_6_12_months_percent',
    ];

    return barOrder.reduce(
      (result, key, index) => {
        const barsAbove = barOrder.slice(0, index);
        const hasBarsAbove = barsAbove.some((barKey) =>
          data.some((item) => (item[barKey as keyof typeof item] as number) > 0)
        );

        result[key] = !hasBarsAbove ? [4, 4, 1, 1] : [1, 1, 1, 1];
        return result;
      },
      {} as Record<keyof typeof chartConfig, [number, number, number, number]>
    );
  }, [data]);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="type-body-xs flex items-center font-medium text-zinc-800">
        <InfoToolTip iconClassName="h-4 w-4" info={TOOLTIP_TEXT}>
          Frequency of Use
        </InfoToolTip>
      </div>
      <div className="type-eyebrow leading-4 text-zinc-500">{`${caseCount.toLocaleString()} case(s) in the last 12 months`}</div>

      <ChartContainer config={chartConfig} className="mt-1 h-full min-h-[230px] w-full">
        <BarChart
          barCategoryGap="30%"
          accessibilityLayer
          data={data}
          className="[&_.recharts-cartesian-grid-horizontal_line]:[stroke-dasharray:none] [&_.recharts-cartesian-grid-vertical_line]:!stroke-zinc-200 [&_.recharts-cartesian-grid-vertical_line]:[stroke-dasharray:3,3]"
        >
          <CartesianGrid
            verticalCoordinatesGenerator={(props) => {
              const xAxis = props.xAxis as
                | {
                    scale: ScaleBand<string>;
                  }
                | undefined;

              if (!xAxis || !xAxis.scale) return [];

              const scale = xAxis.scale;
              const bandwidth = scale.bandwidth ? scale.bandwidth() : 0;

              // grid lines: left edge + bar boundaries (right edge of each bar)
              const coords = [scale.range()[0]]; // leftmost edge
              scale.domain().forEach((entry) => {
                const value = scale(entry);

                if (value !== undefined) {
                  coords.push(value + bandwidth);
                }
              });

              return coords;
            }}
          />

          <XAxis dataKey="count_items" tickLine={false} tickMargin={3} axisLine={false} fontSize={9}>
            <Label value="Units Used" offset={-4} fontSize={9} position="insideBottom" />
          </XAxis>

          <YAxis
            dataKey="count_cases_percent"
            tickLine={false}
            axisLine={false}
            fontSize={9}
            domain={[0, 100]}
            tickCount={5}
            type="number"
            width={40}
            ticks={[0, 50, 100]}
          >
            <Label
              value="% of Cases"
              offset={0}
              angle={-90}
              fontSize={9}
              position="insideLeft"
              style={{ textAnchor: 'middle' }}
            />
          </YAxis>

          {[0, 25, 50, 75, 100].map((val, i) => (
            <ReferenceLine key={i} y={val} />
          ))}

          <ChartLegend
            content={(props) => {
              return (
                <div className="type-eyebrow mt-3 text-zinc-500">
                  <span>Recency of case:</span>
                  <ChartLegendContent className="justify-start pt-2" payload={props.payload} />
                </div>
              );
            }}
          />

          <ChartTooltip
            cursor={false}
            content={(props: any) => {
              const order = Object.keys(chartConfig);
              const sortedPayload = [...props.payload].sort(
                (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey)
              );

              return (
                <ChartTooltipContent
                  {...props}
                  payload={sortedPayload}
                  formatter={(value, name, item, _index) => {
                    const extendedLabel = chartExtendedLabelMap[name as keyof typeof chartExtendedLabelMap];

                    const baseKey = item.dataKey?.toString().replace('_percent', '').replace('_value', '') ?? '';

                    const rawKey = `${baseKey}_value`;
                    const rawValue = item.payload[rawKey];

                    return (
                      <>
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px] border"
                          style={{ backgroundColor: item.color, borderColor: item.color }}
                        />
                        <div className="flex flex-1 items-center justify-between gap-2 leading-none">
                          <span className="font-medium">{extendedLabel}</span>
                          <span>
                            {rawValue} Cases ({value}%)
                          </span>
                        </div>
                      </>
                    );
                  }}
                  labelFormatter={(value) => (
                    <div>
                      <span>{`${value} Units Used`}</span>
                      <hr className="my-1 w-full border-t border-zinc-200" />
                    </div>
                  )}
                />
              );
            }}
          />

          <Bar
            dataKey="count_cases_last_6_12_months_percent"
            stackId="a"
            fill={chartConfig['count_cases_last_6_12_months_percent'].color}
            radius={barRadii.count_cases_last_6_12_months_percent}
            stroke="white"
            strokeWidth={1}
          />
          <Bar
            dataKey="count_cases_last_3_6_months_percent"
            stackId="a"
            fill={chartConfig['count_cases_last_3_6_months_percent'].color}
            radius={barRadii.count_cases_last_3_6_months_percent}
            stroke="white"
            strokeWidth={1}
          />
          <Bar
            dataKey="count_cases_last_3_months_percent"
            stackId="a"
            fill={chartConfig['count_cases_last_3_months_percent'].color}
            radius={barRadii.count_cases_last_3_months_percent}
            stroke="white"
            strokeWidth={1}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}

function formatYAxisValue(caseCount: number, totalCases: number): number {
  const value = formatNumber((caseCount / totalCases) * 100, 1);

  return parseFloat(value);
}
